import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, ElementRef, Output, EventEmitter } from '@angular/core';
import { FocusableOption } from '@angular/cdk/a11y';
import { PhxTileBackground, PhxTileSize } from './phx-tile.types';
import mixinDisabled from '../../mixins/disabled';
import { PHX_SIZE, PHX_BACKGROUND } from '../../constants';

const basePhxTileMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  selector: 'phx-tile',
  templateUrl: './phx-tile.component.html',
  styleUrls: ['./phx-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  inputs: ['phxDisabled'],
  host: {
    class: 'phx-tile',
    '[class.phx-tile-clickable]': 'phxClickable',
    '[class.phx-tile-selected]': 'phxSelected',
    '[class.phx-tile-border]': 'phxBorder',
    '[class.phx-tile-border-states]': 'phxBorderStates',
    '[class.phx-tile-background-states]': 'phxBackgroundStates',
    '[class.phx-tile-disabled]': 'phxDisabled',
    '[class.phx-tile-on-summary]': 'phxBackground === PHX_BACKGROUND.SUMMARY',
    '[class.phx-tile-on-bold]': 'phxBackground === PHX_BACKGROUND.BOLD',
    '[class.phx-tile-on-medium]': 'phxBackground === PHX_BACKGROUND.MEDIUM',
    '[class.phx-tile-on-dark]': 'phxBackground === PHX_BACKGROUND.DARK',
    '[class.phx-tile-on-inverted]': 'phxBackground === PHX_BACKGROUND.INVERTED',
    '[class.phx-tile-on-overlay]': 'phxBackground === PHX_BACKGROUND.OVERLAY',
    '[class.phx-tile-xs]': 'phxSize === PHX_SIZE.XS',
    '[class.phx-tile-sm]': 'phxSize === PHX_SIZE.SM',
    '[class.phx-tile-md]': 'phxSize === PHX_SIZE.MD',
    '[class.phx-tile-lg]': 'phxSize === PHX_SIZE.LG',
    '[class.phx-tile-no-inline-padding]': 'phxRemoveInlinePadding',
    '[class.phx-tile-selector]': 'phxIsUsedAsSelector',
    '[class.phx-tile-error]': 'phxHasError',
    '[attr.tabindex]': 'phxDisabled || !phxClickable ? null : phxTabIndex',
    '(click)': 'onHostClick($event)',
  },
})
export class PhxTileComponent extends basePhxTileMixin implements FocusableOption {
  /**
   * Specifies whether the tile is clickable or not. If it is not clickable then it will not be accessible through tab and won't have hover/pressed states.
   */
  @Input() phxClickable: boolean = false;

  /**
   * Specifies whether the tile is selected or not. It will change the background color of the tile.
   */
  @Input() phxSelected: boolean = false;

  /**
   * Specifies whether the tile has a border or not.
   */
  @Input() phxBorder: boolean = false;

  /**
   * Specifies whether the tile has an error or not.
   */
  @Input() phxHasError: boolean = false;

  /**
   * Specifies whether the tile supports border states (hover/pressed) or not.
   */
  @Input() phxBorderStates: boolean = false;

  /**
   * Specifies whether the tile supports background states (hover/pressed) or not.
   */
  @Input() phxBackgroundStates: boolean = false;

  /**
   * Specifies the background style of the tile.
   */
  @Input() phxBackground: PhxTileBackground = PHX_BACKGROUND.STANDARD;

  /**
   * Specifies the size of the tile. It will change padding and border radius of the tile.
   */
  @Input() phxSize: PhxTileSize;

  /**
   * Specifies whether to remove inline (left/right) padding from the tile or not.
   */
  @Input() phxRemoveInlinePadding: boolean = false;

  /**
   * Specifies the tab index of the tile.
   */
  @Input() phxTabIndex: number = 0;

  /**
   * Specifies whether the tile is used as a selector or not. It will add fixed height based on sizes.
   */
  @Input() phxIsUsedAsSelector: boolean = false;

  /**
   * Event emitted when the tile is clicked. It won't be emitted if the tile is disabled.
   */
  @Output() phxClick: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();


  public readonly PHX_SIZE = PHX_SIZE;
  public readonly PHX_BACKGROUND = PHX_BACKGROUND;

  constructor(private _elemRef: ElementRef) {
    super();
  }

  focus(): void {
    if (this.phxClickable) this._elemRef.nativeElement.focus();
  }

  onHostClick(event: MouseEvent): void {
    if (this.phxDisabled) {
      event.stopPropagation();
      return;
    };
    this.phxClick.emit(event);
  }
}
